import { BoxTypes } from "./BoxTypes";
import { BoxValidations } from "./BoxValidations";

const MONTHS = [
    { label: "Janeiro", value: 1 },
    { label: "Fevereiro", value: 2 },
    { label: "Março", value: 3 },
    { label: "Abril", value: 4 },
    { label: "Maio", value: 5 },
    { label: "Junho", value: 6 },
    { label: "Julho", value: 7 },
    { label: "Agosto", value: 8 },
    { label: "Setembro", value: 9 },
    { label: "Outubro", value: 10 },
    { label: "Novembro", value: 11 },
    { label: "Dezembro", value: 12 },
]

const WEEK_DAYS = [
    { id: 1, day: "Dom.", },
    { id: 2, day: "Seg.", },
    { id: 3, day: "Ter.", },
    { id: 4, day: "Qua.", },
    { id: 5, day: "Qui.", },
    { id: 6, day: "Sex.", },
    { id: 7, day: "Sáb.", },
];

export class FlowBox {

    static getFlowStatusName(value, type) {
        const status = {
            ACTIVE: "Ativa",
            FINISHED: "Encerrada",
            UNDER_ANALYSIS: "Em análise",
            DRAFT: "Rascunho",
            EXPIRED_DRAFT: "Rascunho expirado",
            ARCHIVED: "Arquivado",
            SCHEDULED: "Enviando",
            PROCESSING: "Enviando",
            PAUSED: "Pausada",
            PUBLISHED: type === "RANK_BYPASS" ? "Publicado com prioridade" : "Publicado",
        };
        return status[value] || "Erro";
    }

    static isASource(type) {
        const sources = [
            BoxTypes.FullAudience,
            BoxTypes.SavedAudience,
            BoxTypes.ImportedAudience,
            BoxTypes.AudienceGroup,
            BoxTypes.EventSource,
            BoxTypes.BroadcastAudience,
        ];
        return sources.includes(type)
    }

    static isAGroup(type) {
        const groups = [
            BoxTypes.AudienceGroup,
            BoxTypes.FilterGroup,
            BoxTypes.ConditionalGroup,
            BoxTypes.CreativeGroupAction,
        ];
        return groups.includes(type)
    }

    static isAAction(type) {
        const actions = [
            BoxTypes.SmsCreativeAction,
            BoxTypes.EmailCreativeAction,
            BoxTypes.PushCreativeAction,
            BoxTypes.CreativeGroupAction,
            BoxTypes.WhatsAppCreativeAction,
        ];
        return actions.includes(type)
    }

    static isEventFlow(boxes) {
        return boxes.findIndex((b) => b.data.type == 'EventSource') > -1;
    }

    static validateConnection(boxes, params) {
        const sourceBox = boxes.find((b) => b.id == params.source);
        const targetBox = boxes.find((b) => b.id == params.target);

        if (!sourceBox || !targetBox) {
            return false;
        }

        if (sourceBox?.type == BoxTypes.Trigger) {
            return FlowBox.isASource(targetBox?.type);
        }

        if (FlowBox.isASource(targetBox?.type)) {
            return sourceBox?.type == BoxTypes.Trigger;
        }

        return true;
    }

    static validateGroupDrop(type, targetType) {
        const ALLOW_IN_GROUP = {
            [BoxTypes.AudienceGroup]: [BoxTypes.SavedAudience, BoxTypes.ImportedAudience],
            [BoxTypes.FilterGroup]: [
                BoxTypes.AverageTicketFilter,
                BoxTypes.AgeFilter,
                BoxTypes.RegionFilter,
                BoxTypes.BirthdayFilter,
                BoxTypes.OrderCountFilter,
                BoxTypes.TotalSpendFilter,
                BoxTypes.RegisterTimeFilter,
                BoxTypes.ClimateProfileFilter,
                BoxTypes.LastConversionFilter,
                BoxTypes.ConsumedProductsFilter,
                BoxTypes.ConsumptionMomentsFilter,
                BoxTypes.OriginFilter,
                BoxTypes.BrandsAndUnitsFilter,
                BoxTypes.OrderFilter
            ],
            [BoxTypes.ConditionalGroup]: [
                BoxTypes.AgeConditional,
                BoxTypes.DayOfWeekConditional,
                BoxTypes.DayOfMonthConditional,
                BoxTypes.SpecificDayConditional,
                BoxTypes.SpecificClimateConditional,
                BoxTypes.AverageTicketConditional,
                BoxTypes.OrderCountConditional,
                BoxTypes.TotalSpendConditional,
                BoxTypes.AgeConditional,
                BoxTypes.LastConversionConditional,
                BoxTypes.BirthdayConditional,
                BoxTypes.RegionConditional,
                BoxTypes.RegisterTimeConditional,
                BoxTypes.ClimateProfileConditional,
                BoxTypes.ConsumedProductsConditional,
                BoxTypes.ConsumptionMomentsConditional,
                BoxTypes.OriginConditional,
                BoxTypes.BrandsAndUnitsConditional,
                BoxTypes.OrderConditional
            ],
            [BoxTypes.CreativeGroupAction]: [
                BoxTypes.SmsCreativeAction,
                BoxTypes.EmailCreativeAction,
                BoxTypes.PushCreativeAction,
                BoxTypes.WhatsAppCreativeAction,
            ]
        }

        return ALLOW_IN_GROUP[targetType]?.includes(type) || false;
    }

    static isInsideBox(position, node) {
        const endX = node.position.x + node.width;
        const endY = node.position.y + node.height;
        return (
            position.x >= node.position.x &&
            position.x <= endX &&
            position.y >= node.position.y &&
            position.y <= endY
        )
    }

    /**
     * @name findPathToRoot
     * @param {String} startBoxId 
     * @param {Array<Object>} boxes 
     * @param {Array<Object>} links 
     * @returns {Array<Object>} Boxes e Links
     */
    static findPathToRoot(startBoxId, boxes, links, include_source = false) {
        const startBox = boxes.find(b => b.id == startBoxId);

        if (include_source) {
            if (startBox?.data?.type == BoxTypes.Trigger) {
                return [[], []]
            }
        } else {
            if (FlowBox.isASource(startBox?.data?.type)) {
                return [[], []]
            }
        }

        const inputs = links.filter(f => f.target == startBoxId);
        const responseLinks = [...inputs]
        const responseBoxes = [startBox];

        if (inputs.length == 0) {
            return [responseBoxes, []]
        }

        for (const input of inputs) {
            const [boxArray, linkArray] = FlowBox.findPathToRoot(input.source, boxes, links, include_source);
            responseLinks.push(...(linkArray || []));
            responseBoxes.push(...(boxArray || []))
        }

        const returnBoxes = {}
        for (const b of responseBoxes) {
            if (!returnBoxes[b.id]) {
                returnBoxes[b.id] = b;
            }
        }

        return [Object.values(returnBoxes), responseLinks]
    }

    static interceptClickAndModifyEvent(e, detail) {
        e.stopPropagation();
        e.preventDefault();
        const customEvent = new CustomEvent('click', { bubbles: true, detail })
        e.target.parentNode.dispatchEvent(customEvent);
    }

    static isValidFlow(boxes, links) {
        //Deve haver uma ação e um publico ao menos
        const hasSource = boxes.find((box) => FlowBox.isASource(box.data.type));
        const hasAction = boxes.find((box) => FlowBox.isAAction(box.data.type));

        if (!hasSource || !hasAction) {
            return false;
        }

        //Não deve haver caixas soltas
        for (const box of boxes) {
            if (box.data?.type == BoxTypes.Trigger || FlowBox.isASource(box.data?.type)) {
                continue;
            }

            //Encontra o caminho até a raiz
            const [pathBoxes, _] = FlowBox.findPathToRoot(box.id, boxes, links, true);
            // Verifica se possuí uma fonte
            const hasSource = pathBoxes.find((b) => FlowBox.isASource(b.data.type))
            //Se não possuir, retornar erro
            if (!hasSource) {
                return false;
            }

            if(!BoxValidations.validate(box.data.type, box.data)) {
                return false;
            }
        }

        return true;
    }

    static getSublineByType(boxType, data, variable = '', formatter) {
        switch (boxType) {
            case BoxTypes.AgeFilter:
            case BoxTypes.AgeConditional:
            case BoxTypes.AverageTicketFilter:
            case BoxTypes.AverageTicketConditional:
            case BoxTypes.OrderCountFilter:
            case BoxTypes.OrderCountConditional:
            case BoxTypes.TotalSpendFilter:
            case BoxTypes.TotalSpendConditional:
            case BoxTypes.RegisterTimeFilter:
            case BoxTypes.RegisterTimeConditional:
            case BoxTypes.OrderFrequencyFilter:
            case BoxTypes.OrderFrequencyConditional:
            case BoxTypes.DaysWithoutConversionFilter:
            case BoxTypes.DaysWithoutConversionConditional: {
                let str = variable;

                if (typeof formatter != 'function') {
                    formatter = (v) => v;
                }

                if (data.exact !== undefined) {
                    str = `Exatamente ${formatter(data.exact || 0)} ${variable}`;
                } else if(data.max !== undefined) {
                    str = `Entre ${formatter(data.min || 0)} e ${formatter(data.max || 0)}  ${variable}`;
                } else if(data.min) {
                    str = `Mais de ${formatter(data.min || 0)} ${variable}`;
                }
                
                if (data.inLast && data.inLastPeriod) {
                    const periods = { "DAY": 'dias', "MONTH": 'meses', "YEAR": 'anos' };
                    str = str ? `${str} (${data.inLast} ${periods[data.inLastPeriod]})` : `${data.inLast} ${periods[data.inLastPeriod]}`
                }

                return str != variable ? str : null;
            }
            case BoxTypes.LastConversionFilter:
            case BoxTypes.LastConversionConditional: {
                const periods = { "DAY": 'dias', "MONTH": 'meses', "YEAR": 'anos' };
                if (!(data.inLast && data.inLastPeriod)) return null

                return `Última conversão ≤ ${data.inLast} ${periods[data.inLastPeriod]}`
            }
            case BoxTypes.BirthdayFilter:
            case BoxTypes.BirthdayConditional: {
                switch (data.moment) {
                    case 'DAY': return "Dia atual";
                    case 'DIFF_NEXT_DATE': return `${data.value} dias antes`;
                    case 'MONTH': return (MONTHS.find((m) => data.value == m.value))?.label
                    default: return "";
                }
            }
            case BoxTypes.RegionFilter:
            case BoxTypes.RegionConditional: {
                return data?.regions?.length > 0 ? `${data.regions.length} regiões selecionadas` : null;
            }
            case BoxTypes.RFVProfileFilter:
            case BoxTypes.RFVProfileConditional: {
                return data?.values?.length > 0 ? `${data.values.length} perfis selecionados` : null;
            }
            case BoxTypes.BrandsAndUnitsFilter:
            case BoxTypes.BrandsAndUnitsConditional: {
                return data?.units?.length > 0 ? `${data.units.length} unidades selecionadas` : null;
            }
            case BoxTypes.OriginFilter:
            case BoxTypes.OriginConditional: {
                return data?.origins?.length > 0 ? `${data.origins.length} origens selecionadas` : null;
            }
            case BoxTypes.DayOfWeekConditional: {
                return data?.days?.length > 0 ? `${WEEK_DAYS.filter(d => data.days.includes(d.id)).map((s) => s.day).join(', ')}` : null;
            }
            case BoxTypes.DayOfMonthConditional: {
                return data?.days?.length > 0 ? `Dias: ${data?.days?.join(', ')}` : null;
            }
            case BoxTypes.SpecificDayConditional: {
                return data?.date || null;
            }
            case BoxTypes.WhatsAppCreativeAction:
            case BoxTypes.PushCreativeAction:
            case BoxTypes.SmsCreativeAction:
            case BoxTypes.EmailCreativeAction: {
                return data.creative?.name || null;
            }
            case BoxTypes.BroadcastAudience:
                if (data?.broadcast?.type === 'ALL') {
                    return 'Todas as regiões'
                } else {
                    return data?.broadcast?.states?.length > 0 ? `${data.broadcast.states.length} regiões selecionadas` : null;
                }
            case BoxTypes.CustomerPhoneFilter:
                return (data.phone || []).join(', ');
            default:
                return null;
        }
    }
}